/**
 * 1.6.3 Administration → Shared links: every share link in the workspace (pages of 50, with counts per state and how
 * far back the view history goes), a link's view history (pages of 100) and revoking several links at once.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdminShareRevokeResult, AdminShareSort, AdminSharesPage, AdminShareStateFilter, ShareViewDTO, ShareViewsPage } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { usePaged } from './paged';

export function useAdminShares(opts: { state: AdminShareStateFilter; q: string; sort: AdminShareSort }) {
  const params = new URLSearchParams({ state: opts.state, sort: opts.sort });
  if (opts.q.trim()) params.set('q', opts.q.trim());
  return usePaged<AdminSharesPage['items'][number], AdminSharesPage>({
    queryKey: queryKeys.sharesAdmin(params.toString()),
    url: `/api/v1/admin/shares?${params}`,
    pageSize: 50,
  });
}

export function useShareViews(shareId: string | null) {
  return usePaged<ShareViewDTO, ShareViewsPage>({
    queryKey: queryKeys.shareViews(shareId ?? ''),
    url: `/api/v1/admin/shares/${shareId}/views`,
    pageSize: 100,
    enabled: Boolean(shareId),
  });
}

export function useRevokeShares() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api<AdminShareRevokeResult>('/api/v1/admin/shares/revoke', { method: 'POST', json: { ids } }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.sharesAdminAll() }),
      // The assets' Sharing tabs and the approvals the links asked for.
      qc.invalidateQueries({ queryKey: ['shares'] }),
      qc.invalidateQueries({ queryKey: queryKeys.approvalsAll() }),
    ]),
  });
}
